/**
 * Token Expiry Utilities
 *
 * Monitors JWT expiry and warns users 5 minutes before session expires.
 * On expiry, redirects to login.
 */

import { getStoredToken, clearStoredToken } from './tokenStorage';

let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let warningTimer: ReturnType<typeof setTimeout> | null = null;
let warningCallback: (() => void) | null = null;

/** Decode JWT payload without validation (client-side only) */
function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Get token expiry as Date, or null if invalid */
export function getTokenExpiry(): Date | null {
  const token = getStoredToken();
  if (!token) return null;
  const payload = decodeTokenPayload(token);
  if (!payload || typeof payload.exp !== 'number') return null;
  return new Date(payload.exp * 1000);
}

/** Get remaining session time in seconds */
export function getSessionTimeRemaining(): number {
  const expiry = getTokenExpiry();
  if (!expiry) return 0;
  return Math.max(0, Math.floor((expiry.getTime() - Date.now()) / 1000));
}

/**
 * Start monitoring token expiry.
 * - Calls onWarning 5 minutes before expiry
 * - Clears token and redirects to login on expiry
 */
export function startExpiryMonitor(onWarning?: () => void): void {
  stopExpiryMonitor();

  warningCallback = onWarning || null;
  const remaining = getSessionTimeRemaining();
  if (remaining <= 0) return;

  const WARNING_BEFORE_SECONDS = 300; // 5 minutes

  // Set warning timer
  if (remaining > WARNING_BEFORE_SECONDS && onWarning) {
    const warningMs = (remaining - WARNING_BEFORE_SECONDS) * 1000;
    warningTimer = setTimeout(() => {
      if (warningCallback) warningCallback();
    }, warningMs);
  }

  // Set expiry timer
  const expiryMs = remaining * 1000;
  expiryTimer = setTimeout(() => {
    clearStoredToken();
    window.location.href = '/login';
  }, expiryMs);
}

/** Stop monitoring */
export function stopExpiryMonitor(): void {
  if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
  if (warningTimer) { clearTimeout(warningTimer); warningTimer = null; }
  warningCallback = null;
}
